'use strict';

// Base64 polyfill
import Base64 from 'Base64';
import store from 'store';
import api from 'utils/api';
import dom from 'utils/dom';
import Immutable from 'immutable';
import { assign } from 'lodash';
import flux from 'utils/flux';
import cookie from 'js-cookie';
import mb_consts from 'utils/mb_consts';

const AppSource = (alt) => {
    return {
        loginUser: {
            remote(state, user) {
                return api.post('login', user);
            },
            success: alt.getActions('app').loginUserSuccess,
            error: alt.getActions('app').onError
        },
        logoutUser: {
            remote(state, user) {
                if (!state.inLogout) {
                    state.inLogout = true;
                    const token = store.get('mediabox_token');
                    return api.post('logout', { token }, alt.getActions('app').logoutUserSuccess);
                }
            },
            success: alt.getActions('app').logoutUserSuccess,
            error: alt.getActions('app').onError
        },
        acceptTerms: {
            remote(state, data) {
                return api.post('user', data);
            },
            success: alt.getActions('app').acceptTermsSuccess,
            error: alt.getActions('app').onError
        },
        registerUser: {
            remote(state, data) {
                return api.post('register', data);
            },
            success: alt.getActions('app').registerUserSuccess,
            error: alt.getActions('app').onError
        },
        resetPassword: {
            remote(state, data) {
                return api.post('reset_password', data);
            },
            success: alt.getActions('app').resetPasswordSuccess,
            error: alt.getActions('app').onError
        },
        updatePassword: {
            remote(state, data) {
                return api.post('change_password', data);
            },
            success: alt.getActions('app').updatePasswordSuccess,
            error: alt.getActions('app').onError
        },
        confirmEmail: {
            remote(state, data) {
                return api.post('confirm_email', data);
            },
            success: alt.getActions('app').confirmEmailSuccess,
            error: alt.getActions('app').onError
        },
        loadAccountData: {
            remote() {
                return api.get('user');
            },
            success: alt.getActions('app').loadAccountDataSuccess,
            error: alt.getActions('app').onError
        },
        updateAccountData: {
            remote(state, data) {
                return api.post('edit_user', data).then(() => {
                    return api.get('user').then(alt.getActions('app').loadAccountDataSuccess);
                });
            },
            success: alt.getActions('app').noop,
            error: alt.getActions('app').onError
        },
        bootstrap: {
            remote() {
                return api.get('bootstrap');
            },
            success: alt.getActions('app').bootstrapRegisterFormSuccess,
            error: alt.getActions('app').onError
        },
        loadTerms: {
            local(state) {
                return state.terms;
            },
            remote() {
                return api.getHtml('terms-of-use');
            },
            success: alt.getActions('app').loadTermsSuccess,
            error: alt.getActions('app').onError
        },
        loadPrivacy: {
            local(state) {
                return state.privacy;
            },
            remote() {
                return api.getHtml('privacy-policy');
            },
            success: alt.getActions('app').loadPrivacySuccess,
            error: alt.getActions('app').onError
        },
        loadImprint: {
            local(state) {
                return state.imprint;
            },
            remote() {
                return api.getHtml('imprint');
            },
            success: alt.getActions('app').loadImprintSuccess,
            error: alt.getActions('app').onError
        }
    };
};

export default class AppStore {

    constructor() {
        this.bindActions(this.alt.getActions('app'));
        this.registerAsync(AppSource);
        this.exportPublicMethods({
            logout: this.onLogout,
            setMediaboxForward: this.setMediaboxForward,
            doMediaboxForward: this.doMediaboxForward,
        });

        if (window.config.samlLogin) {
            store.set('mediabox_saml_login', true);
        } else {
            store.set('mediabox_saml_login', false);
        }

        if (window.config.noneLogin) {
            store.set('mediabox_none_login', true);
        } else {
            store.set('mediabox_none_login', false);
        }

        if (window.config.hideRegistration) {
            store.set('mediabox_hide_registration', true);
        } else {
            store.set('mediabox_hide_registration', false);
        }

        if (cookie.get('basicauth_token') !== undefined) {
            if (!window.config.showTermsOfUse) {
                store.set('mediabox_terms_accepted', true);
            }
            store.set('mediabox_token', cookie.get('basicauth_token'));
        }

        this.termsAccepted = store.get('mediabox_terms_accepted');
        this.token = cookie.get('basicauth_token') || store.get('mediabox_token');
        this.user = store.get('mediabox_user');
        this.branding = store.get('mediabox_branding');
        this.accountEditable = store.get('mediabox_account_editable');
        this.passwordEditable = store.get('mediabox_password_editable');
        this.hideFilters = store.get('mediabox_hideFilters');
        this.samlLogin = store.get('mediabox_saml_login');
        this.noneLogin = store.get('mediabox_none_login');
        this.hideRegistration = store.get('mediabox_hide_registration');
        this.mediaboxForward = store.get('mediabox_forward_to');
        this.inLogout = false;
        this.mediaboxRights = store.get('mediabox_rights');
        this.excel_rights = store.get('excel_rights');

        if (this.token) {
            api.registerToken(this.token);
        }

        const buildConfig = ('production' === process.env.NODE_ENV && require('../config')) || {};
        this.config = Immutable.fromJS(assign({}, window.config, buildConfig));

        this.error = false;
        this.accountData = false;
        this.terms = null;
        this.registerForm = null;
    }

    onError(err) {
        this.inLogout = false;
        err = JSON.parse(err.response);
        this.error = err.message;
    }

    onClearError() {
        this.error = null;
    }

    onAcceptTermsSuccess(data) {
        store.set('mediabox_terms_accepted', true);
        this.termsAccepted = true;
    }

    onLoginUserSuccess(data) {
        const { access_token, full_name, account_editable, password_editable, termsAccepted, branding, hideFilters, language, mediabox_rights, excel_rights } = data;

        store.set('mediabox_terms_accepted', termsAccepted);
        store.set('mediabox_token', access_token);
        store.set('mediabox_user', full_name);
        store.set('mediabox_branding', branding);
        store.set(mb_consts.STORE_LANGUAGE_KEY, language);
        store.set('mediabox_account_editable', account_editable);
        store.set('mediabox_password_editable', password_editable);
        store.set('mediabox_hideFilters', hideFilters);
        store.set('_CSRF', data._CSRF);
        store.set('mediabox_rights', mediabox_rights);
        store.set('excel_rights', excel_rights);

        this.termsAccepted = termsAccepted;
        this.token = access_token;
        this.user = full_name;
        this.accountEditable = account_editable;
        this.passwordEditable = password_editable;
        this.branding = branding;
        this.hideFilters = hideFilters;
        this.error = false;
        this.inLogout = false;
        this.mediaboxRights = mediabox_rights;
        this.excel_rights = excel_rights;

        api.registerToken(this.token);
    }

    onLoadAccountDataSuccess(data) {
        this.error = false;
        this.accountData = data;
    }

    noop() {
    }

    onLoadTermsSuccess(terms) {
        this.terms = terms;
    }

    onLoadPrivacySuccess(privacy) {
        this.privacy = privacy;
    }

    onLoadImprintSuccess(imprint) {
        this.imprint = imprint;
    }

    onBootstrapRegisterFormSuccess(registerForm) {
        this.registerForm = registerForm;
    }

    onLogout() {
        flux.getStore('app').logoutUser();
    }

    onLogoutUserSuccess() {
        this.token = null;
        this.user = null;
        this.inLogout = false;

        api.removeToken();
        var userLocale = store.get(mb_consts.STORE_LANGUAGE_KEY);
        store.clear();
        cookie.remove('basicauth_token');
        store.set(mb_consts.STORE_LANGUAGE_KEY, userLocale);

        // empty caches
        flux.getStore('media').state._cache = {};

        if (arguments.length > 0 && !!arguments[0].redirect) {
            window.location = arguments[0].redirect;
        } else {
            window.location = `${ location.protocol }//${ location.host }`;
        }
    }

    setMediaboxForward(mediaboxForward) {
        if (this.state.token === undefined) {
            this.mediaboxForward = mediaboxForward;
            store.set('mediabox_forward_to', mediaboxForward);
            console.log("mediaboxForward in store set to: ", this.mediaboxForward);
        } else {
            this.mediaboxForward = undefined;
            store.remove('mediabox_forward_to');
            console.log("mediaboxForward nulled out in store");
        }
    }

    doMediaboxForward() {
        this.mediaboxForward = store.get('mediabox_forward_to');
        console.log("got from store: ", this.mediaboxForward);

        if (this.mediaboxForward !== undefined) {
            console.log("going to window.location redirect", this.mediaboxForward);
            window.location = this.mediaboxForward;
        }

        return this.mediaboxForward;
    }

    static toggleOffcanvas() {
        var bodyEl = document.getElementsByTagName('body')[0];
        dom.toggleClass(bodyEl, 'offcanvas--active');
    }

    static closeOffcanvas() {
        var bodyEl = document.getElementsByTagName('body')[0];
        dom.removeClass(bodyEl, 'offcanvas--active');
    }

    static isAuthenticated() {
        var { token } = this.getState();
        if (!token) {
            return false;
        }
        return true;
    }

    static isNoneLogin() {
        var { noneLogin } = this.getState();
        return noneLogin;
    }

    static isSamlLogin() {
        var { samlLogin } = this.getState();
        return samlLogin;
    }

    static hasAcceptedLatestTerms() {
        var { termsAccepted } = this.getState();
        return termsAccepted;
    }
}



// WEBPACK FOOTER //
// ./app/stores/appStore.js